import React from 'react';
import { Box, Chip, Divider, Paper, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import AssignmentIcon from "@mui/icons-material/Assignment";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { getAssessmentSeverityLabel } from './dashboardDisplayUtils';

// Shared presentation: callers supply data and existing event handlers.
export default function PatientDashboardOverview({ assessmentGroups, assessmentsLoading, assessmentsRef, chatSummariesRef, getAssessmentMeta, getSeverityColor, latestRiskSignals, latestRiskStyle, latestScan, latestScanAverage, latestSession, scansLoading, scrollTo, vitalSummary, wellnessRef }) {
return (<Paper
        elevation={0}
        sx={{
            p: 3,
            borderRadius: 2,
            border: '1px solid #e6e6e6',
            mb: 4,
            backgroundColor: '#ffffff',
        }}
        >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Overview
            </Typography>
        </Stack>

        <Stack spacing={2}>
            {/* Active Risk Flags - Stays Full Width */}
            <Box
            data-tour="demo-dashboard-risk"
            sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: latestRiskStyle.bg,
                border: `1px solid ${latestRiskStyle.border}`,
            }}
            >
            <Stack direction="row" spacing={2} alignItems="center">
                <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: latestRiskStyle.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: latestRiskStyle.text,
                    border: `1px solid ${latestRiskStyle.border}`,
                }}
                >
                <WarningAmberIcon />
                </Box>
                <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Active Risk Flags
                </Typography>
                {latestRiskSignals.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                    No active risk flags.
                    </Typography>
                ) : (
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5, rowGap: 1.5 }}>
                    {latestRiskSignals.map((flag, idx) => (
                        <Chip
                        key={`${flag}-${idx}`}
                        label={flag}
                        size="small"
                        sx={{
                            backgroundColor: '#fff',
                            color: latestRiskStyle.text,
                            border: `1px solid ${latestRiskStyle.border}`,
                            fontWeight: 600,
                        }}
                        />
                    ))}
                    </Stack>
                )}
                </Box>
            </Stack>
            </Box>

            {/* 3-Column Grid for Summaries */}
            <Grid container spacing={2}>
                {/* Chat Summaries Card */}
                <Grid size={{xs: 12, md: 4}}>
                    <Box
                    onClick={() => scrollTo(chatSummariesRef)}
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: '#EFF6FF',
                        border: '1px solid #BFDBFE',
                        height: '100%',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.15)',
                            transform: 'translateY(-2px)'
                        }
                    }}
                    >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                        <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            backgroundColor: '#DBEAFE',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#2563EB',
                        }}
                        >
                        <ChatBubbleOutlineIcon />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Chat Summaries
                        </Typography>
                        </Box>
                    </Stack>

                    <Divider sx={{ my: 1, borderColor: 'rgba(37, 99, 235, 0.2)' }} />

                    {/* Latest Chat Section */}
                    {latestSession ? (
                        <Box sx={{ mt: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2563EB' }}>
                            Latest Chat
                            </Typography>
                            {latestRiskSignals.length > 0 && (
                            <Chip
                                label={`${latestRiskSignals.length} Crisis Alert${latestRiskSignals.length > 1 ? 's' : ''}`}
                                size="small"
                                sx={{ backgroundColor: '#FEE2E2', color: '#B91C1C', fontWeight: 600, height: 20, fontSize: '0.65rem' }}
                            />
                            )}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5, fontSize: '0.85rem' }}>
                            {latestSession?.emotions?.length > 0 && (
                            <span style={{ display: 'block', marginBottom: '4px' }}>
                                • <strong>Emotions:</strong> {latestSession.emotions.join(', ')}
                            </span>
                            )}
                            {latestSession?.key_themes?.length > 0 && (
                            <span>
                                • <strong>Themes:</strong> {latestSession.key_themes.slice(0, 3).join(', ')}
                                {latestSession.key_themes.length > 3 && '...'}
                            </span>
                            )}
                        </Typography>
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            No chat sessions available.
                        </Typography>
                    )}
                    </Box>
                </Grid>

                {/* Wellness Overview Card */}
                <Grid size={{xs: 12, md: 4}}>
                    <Box
                    onClick={() => scrollTo(wellnessRef)}
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: '#ECFDF3',
                        border: '1px solid #BBF7D0',
                        height: '100%',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            boxShadow: '0 4px 12px rgba(5, 150, 105, 0.15)',
                            transform: 'translateY(-2px)'
                        }
                    }}
                    >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                        <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            backgroundColor: '#D1FAE5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#059669',
                        }}
                        >
                        <MonitorHeartIcon />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Wellness Overview
                        </Typography>
                        </Box>
                    </Stack>
                    
                    <Divider sx={{ my: 1, borderColor: 'rgba(5, 150, 105, 0.2)' }} />

                    {/* Latest Scan Section */}
                    {scansLoading ? (
                        <Typography variant="body2" color="text.secondary">
                        Loading scans...
                        </Typography>
                    ) : latestScan ? (
                        <Box sx={{ mt: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#059669' }}>
                            Latest Scan
                            </Typography>
                            {vitalSummary?.stressCritical && (
                            <Chip
                                label="High Stress"
                                size="small"
                                sx={{ backgroundColor: '#FEE2E2', color: '#B91C1C', fontWeight: 600, height: 20, fontSize: '0.65rem' }}
                            />
                            )}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.85rem' }}>
                        {`• Average score: ${
                            latestScanAverage != null ? `${latestScanAverage.toFixed(1)}/5` : '--'
                        }`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5, fontSize: '0.85rem', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {vitalSummary?.statusSummary ? (
                                <>• {vitalSummary.statusSummary}</>
                            ) : (
                                <>• No vital sign status available.</>
                            )}
                        </Typography>
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                        No scans available.
                        </Typography>
                    )}
                    </Box>
                </Grid>

                {/* Assessments Card (New) */}
                <Grid size={{xs: 12, md: 4}}>
                    <Box
                    onClick={() => scrollTo(assessmentsRef)}
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: '#F5F3FF',
                        border: '1px solid #E9D5FF',
                        height: '100%',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            boxShadow: '0 4px 12px rgba(124, 58, 237, 0.15)',
                            transform: 'translateY(-2px)'
                        }
                    }}
                    >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                        <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            backgroundColor: '#E9D5FF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#7C3AED',
                        }}
                        >
                        <AssignmentIcon />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Assessments
                        </Typography>
                        </Box>
                    </Stack>

                    <Divider sx={{ my: 1, borderColor: 'rgba(124, 58, 237, 0.2)' }} />

                    {assessmentsLoading ? (
                        <Typography variant="body2" color="text.secondary">
                        Loading assessments...
                        </Typography>
                    ) : Object.keys(assessmentGroups).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                        No assessments available.
                        </Typography>
                    ) : (
                        <Box sx={{ mt: 1 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#7C3AED' }}>
                                Latest Assessments
                                </Typography>
                            </Stack>
                            <Stack spacing={1.5}>
                                {Object.entries(assessmentGroups).slice(0, 3).map(([type, history]) => {
                                    const latest = history[0];
                                    const meta = getAssessmentMeta(type);
                                    const severityLabel = getAssessmentSeverityLabel(type, latest);
                                    return (
                                        <Box key={type} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                            <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
                                                {meta.label}
                                            </Typography>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 'bold', lineHeight: 1 }}>
                                                    {latest.total_score}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: getSeverityColor(severityLabel), fontSize: '0.7rem', fontWeight: 600 }}>
                                                    {severityLabel}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )
                                })}
                                {Object.keys(assessmentGroups).length > 3 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ pt: 0.5, display: 'block', textAlign: 'center' }}>
                                        + {Object.keys(assessmentGroups).length - 3} more...
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                    )}
                    </Box>
                </Grid>
            </Grid>
        </Stack>
        </Paper>);
}
